import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PALETTE = ["#5C8065", "#D26A59", "#E8B253", "#829399", "#C5A880", "#1F4E3D", "#A78A5C", "#7A5E48"];

export default function Categories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "expense", color: PALETTE[0] });

  const load = async () => {
    try {
      const r = await api.get("/categories");
      setItems(r.data);
    } catch (e) {
      console.warn("Categories load failed", e?.response?.status);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name required");
      return;
    }
    try {
      await api.post("/categories", { ...form, name: form.name.trim() });
      toast.success("Category added");
      setForm({ name: "", type: "expense", color: PALETTE[0] });
      setOpen(false);
      load();
    } catch {
      toast.error("Could not add");
    }
  };

  const remove = async (c) => {
    if (c.is_default) {
      toast.error("Default categories can't be removed");
      return;
    }
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const expense = items.filter((c) => c.type === "expense");
  const income = items.filter((c) => c.type === "income");

  const renderGroup = (title, list) => (
    <div className="bg-white border border-[#EAE8E3] rounded-lg p-6">
      <div className="label-overline mb-4">{title}</div>
      <ul className="divide-y divide-[#EAE8E3]" data-testid={`cat-list-${title.toLowerCase()}`}>
        {list.map((c) => (
          <li key={c.id} className="flex items-center justify-between py-3">
            <span className="flex items-center gap-2.5 text-sm">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ background: c.color || "#5C8065" }}
              />
              {c.name}
              {c.is_default && (
                <span className="label-overline text-[10px] tracking-[0.15em] text-[#696E69]/70">
                  default
                </span>
              )}
            </span>
            {!c.is_default && (
              <button
                onClick={() => remove(c)}
                data-testid={`delete-cat-${c.id}`}
                className="p-1.5 rounded hover:bg-[#D26A59]/10 text-[#D26A59]"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div data-testid="categories-page">
      <SectionHeader
        overline="Taxonomy"
        title="Categories"
        description="Built-in essentials, plus your own labels for the way you actually spend."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="open-add-category"
                className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
              >
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Add category
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight font-medium">
                  New category
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Name</Label>
                  <Input
                    data-testid="cat-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5"
                    placeholder="e.g. Coffee, Side hustle"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="cat-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Color</Label>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {PALETTE.map((c) => (
                      <button
                        key={c}
                        type="button"
                        data-testid={`color-${c}`}
                        onClick={() => setForm({ ...form, color: c })}
                        className={`w-8 h-8 rounded-full transition-transform ${
                          form.color === c ? "ring-2 ring-offset-2 ring-[#1F4E3D] scale-110" : ""
                        }`}
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button
                    type="submit"
                    data-testid="save-category"
                    className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
                  >
                    Save
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderGroup("Expense", expense)}
        {renderGroup("Income", income)}
      </div>
    </div>
  );
}
